import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay, finalize, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../../../../projects/my-app/src/environments/environment';
import {
  computeSelfServiceData,
  SelfServiceFilters,
  SelfServiceWidgetMeta,
} from '../utils/self-service-data.utils';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  protected router = inject(Router);
  // simple in-memory map to deduplicate identical concurrent requests
  protected inProgressRequests: Map<string, Observable<any>> = new Map();


  /**
   * In-flight request cache.
   * Key = method + URL + payload string + query params.
   */
  private inFlightRequests = new Map<string, Observable<any>>();

  private resolveHeaders(data?: any, filters?: any): Record<string, string> {
    const providedHeaders = {
      ...(data?.headers || {}),
      ...(filters?.headers || {}),
    } as Record<string, string>;

    return providedHeaders;
  }

  /** Public wrapper so callers issuing their own requests (e.g. blob downloads) share the base-URL rules. */
  resolveUrl(url: string): string {
    return this.resolveRequestUrl(url);
  }

  /** Resolve a build-time environment value without hardcoding it in widget JSON. */
  resolveEnvironmentValue(key: string): string {
    const normalizedKey = String(key || '').trim();
    return normalizedKey ? String((environment as Record<string, unknown>)[normalizedKey] ?? '').trim() : '';
  }

  private resolveRequestUrl(url: string): string {
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    const isAbsoluteUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);

    if (isLocalAssets || isAbsoluteUrl) {
      return url;
    }

    const baseUrl = environment.URL.endsWith('/')
      ? environment.URL.slice(0, -1)
      : environment.URL;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  }

  constructor(protected http: HttpClient) { }

  /** Exclude internal keys (UI state, widget records) from API request params */
  private static readonly INTERNAL_KEYS = new Set(['loading']);
  protected cleanRequestParams(params: any, excludeKeys?: Set<string>): any {
    if (!params) return {};
    const cleaned: any = {};
    for (const key of Object.keys(params)) {
      if (ConfigService.INTERNAL_KEYS.has(key)) continue;
      if (key.startsWith('widget-')) continue;
      if (excludeKeys?.has(key)) continue;
      cleaned[key] = params[key];
    }
    return cleaned;
  }

  /**
   * Widget-declared `excludeRequestKeys`: global-filter keys that are page state
   * rather than data — toggle buttons publish their own key and their
   * `toggleGroup` into the same bag the payload is built from, and those must
   * not reach the backend.
   */
  private resolveExcludedRequestKeys(data: any): Set<string> | undefined {
    const raw = data?.excludeRequestKeys;
    if (!raw) return undefined;

    const keys = (Array.isArray(raw) ? raw : [raw])
      .map((key) => String(key).trim())
      .filter(Boolean);

    return keys.length ? new Set(keys) : undefined;
  }

  getDashboardConfig(configFileName: string): Observable<any> {
    return this.http.get(`/assets/dashboard-config/${configFileName}`);
  }

  protected buildPayload(requestParamFilters: any): any {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
    if (
      dashboardName === 'europe-impact-report' &&
      (!requestParamFilters || Object.keys(requestParamFilters).length === 0)
    ) {
      return {
        filter: {
          where: [],
          join: {},
          orderBy: {},
        },
      };
    }
    let staticFilters: any[] = [];
    const customerNameValue = requestParamFilters?.['customer_name'];

    if (dashboardName === 'customer-overview') {
      // added condition to filter out default customername in payload if customer name is selected
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
        ];
      }
    } else if (dashboardName === 'cspec-bridge') {
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    } else if (
      dashboardName !== 'opportunity-overview' &&
      dashboardName !== 'europe-impact-report'
    ) {
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    }

    const dynamicFilters = Object.entries(requestParamFilters)
      .map(([key, value]) => {
        const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value]; // recursively flatten the array to any depth eg. [[[]]].
        let updatedValues = rawValues.filter(
          (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
        );

        if (updatedValues?.length === 0) return null;

        return {
          attributeName: key,
          operator: updatedValues.length > 1 ? 'IN' : '=',
          attributeValues: updatedValues,
          logicalOperator: 'AND',
        };
      })
      .filter((filter): filter is NonNullable<typeof filter> => filter !== null); //emoves null entries from the array.

    // Adjust logicalOperator handling
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
      }
    } else {
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = '';
      }
    }

    return {
      filter: {
        where: [...staticFilters, ...dynamicFilters],
        join: {},
        orderBy: {},
      },
    };
  }

  protected buildQueryParams(queryParamFilters: any): HttpParams {
    let params = new HttpParams();
    for (const key in queryParamFilters) {
      const value = queryParamFilters[key];

      if (key === 'slider_range' && Array.isArray(value) && value.length === 2) {
        const start = parseInt(value[0], 10);
        const end = parseInt(value[1], 10);

        params = params.set('startRange', start.toString());
        params = params.set('endRange', end.toString());
      } else if (key === 'button') {
        params = params.set(value[0], true);
      } else {
        params = params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    }

    return params;
  }

  getConfig(
    url: string,
    data?: any,
    filters?: any,
    method?: string,
    isTable = false,
  ): Observable<any> {

    // Self-service published-dashboard short-circuit: filter raw rows by the
    // current global / cross-filter selections, re-aggregate, and reshape per
    // chart type. This is what makes the published view actually reactive to
    // filter changes without a backend roundtrip.
    if (data?.selfService) {
      const meta = data.selfService as SelfServiceWidgetMeta;
      const ssFilters: SelfServiceFilters = {
        requestParams: filters?.requestParams,
        queryParams: filters?.queryParams,
        sourceWidgetId: filters?.sourceWidgetId ?? null,
      };
      return of(computeSelfServiceData(meta, ssFilters));
    }

    if (!url) {
      return of(this.unwrapResponse(data?.data ?? data?.widgetData ?? data ?? {}));
    }

    const requestHeaders = this.resolveHeaders(data, filters);

    const queryParamsForHttp = { ...(filters?.queryParams || {}) };
    const rawQueryString = filters?.rawQueryString;

    // Embed Query Params into URL
    if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
      const embeddedParts: string[] = [];
      for (const paramName of data.embeddedParams) {
        if (queryParamsForHttp[paramName]) {
          embeddedParts.push(queryParamsForHttp[paramName]);
          delete queryParamsForHttp[paramName];
        }
      }
      if (embeddedParts.length > 0) {
        const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        url = [baseUrl, ...embeddedParts].join('/');
      }
    }

    // Payload Logic
    let payload: any = null;
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};

    const excludedRequestKeys = this.resolveExcludedRequestKeys(data);
    const mappedRequestParams = this.remapRequestParams(
      filters?.requestParams,
      data?.requestParamKeyMap,
    );
    const filteredRequestParams = this.cleanRequestParams(
      mappedRequestParams,
      excludedRequestKeys,
    );
    const filteredCustomPayload = this.cleanRequestParams(
      data?.customPayload,
      excludedRequestKeys,
    );
    const hasFilteredRequestParams = Object.keys(filteredRequestParams).length > 0;

    if (data?.skipDefaultPayload) {
      // When skipDefaultPayload is true, still merge filters into customPayload
      payload = JSON.parse(JSON.stringify(data?.customPayload || {}));

      // Merge request params into the payload's requestData
      if (hasFilteredRequestParams && payload.requestData) {
        if (data?.requestParamsAsFilterRules) {
          payload.requestData.filters = this.mergeRequestParamsAsFilterRules(
            payload.requestData.filters,
            filteredRequestParams,
            data.requestParamsAsFilterRules,
          );
          Object.assign(
            payload.requestData,
            this.pickRequestParams(
              filteredRequestParams,
              data.requestParamsAsFilterRules.passthroughKeys,
            ),
          );
        } else {
          payload.requestData = {
            ...payload.requestData,
            ...filteredRequestParams,
          };
        }
      }
    } else {
      const mergedRequestData = data?.requestParamsOverrideCustomPayload === true
        ? {
            ...filteredCustomPayload,
            ...filteredRequestParams,
          }
        : {
            ...filteredRequestParams,
            ...filteredCustomPayload,
          };

      payload = {
        requestData: {
          ...(!hasFilteredRequestParams ? defaultRequestData : {}),
          ...mergedRequestData,
        },
        responseList: [],
        entity: data?.entity || 'default_entity',
        workflowId: data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: data?.functionName || 'DefaultService',
            version: data?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: data?.version || '1.0',
      };
    }

    // `excludeRequestKeys` applies to the final requestData as well as the
    // incoming filter bag. This prevents a disallowed UI/transient key already
    // present in customPayload.requestData from leaking into the request.
    if (excludedRequestKeys && payload?.requestData && typeof payload.requestData === 'object') {
      excludedRequestKeys.forEach((key) => delete payload.requestData[key]);
    }

    // Query Params Logic
    const useQueryParams = filters?.useQueryParams === true || data?.useQueryParam === true;
    let queryParams = new HttpParams();

    if (useQueryParams) {
      if (rawQueryString) {
        url = `${url}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    if (filters && 'useQueryParams' in filters) {
      delete filters.useQueryParams;
    }

    if (!!isTable) {
      // Table specific logic if needed in future
    }

    // Prepend Base URL only for relative API paths.
    url = this.resolveRequestUrl(url);

    // Determine HTTP Method
    const requestMethod = String(method || data?.method || 'post').toLowerCase();

    const dedupKey = `${requestMethod.toUpperCase()}|${url}|${JSON.stringify(payload)}|${queryParams.toString()}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = (
      requestMethod === 'post'
        ? this.http.post<any>(url, payload, { params: queryParams, headers: requestHeaders })
        : this.http.get<any>(url, { params: queryParams, headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  /**
   * Maps the current UI selections into a nested requestData.filters array.
   * Static rules are retained in their configured order; dynamic rules are
   * inserted before pagination/order rules so every widget can share and
   * deduplicate the same request payload.
   */
  private mergeRequestParamsAsFilterRules(
    configuredFilters: unknown,
    requestParams: Record<string, any>,
    options: any,
  ): any[] {
    const baseFilters = Array.isArray(configuredFilters)
      ? JSON.parse(JSON.stringify(configuredFilters))
      : [];
    const allowedKeys = new Set(
      (Array.isArray(options?.filterKeys) ? options.filterKeys : Object.keys(requestParams))
        .map((key: unknown) => String(key).trim())
        .filter(Boolean),
    );
    const dynamicRules = Object.entries(requestParams).flatMap(([key, rawValue]) => {
      if (!allowedKeys.has(key)) return [];
      const values = (Array.isArray(rawValue) ? rawValue : [rawValue])
        .flat(Infinity)
        .filter((value) => value !== null && value !== undefined && value !== '');
      if (!values.length) return [];

      return [{
        attributeName: key,
        operator: values.length > 1
          ? String(options?.multipleOperator || 'in')
          : '=',
        logicalOperator: '',
        beginScope: '',
        endScope: '',
        attributeValue: options?.attributeValueAsArray === true
          ? values
          : values.length > 1 ? values : values[0],
      }];
    });

    if (!dynamicRules.length) return baseFilters;

    dynamicRules.forEach((rule, index) => {
      rule.logicalOperator = options?.firstLogicalOperator !== undefined
        ? String(index === 0
          ? options.firstLogicalOperator
          : options?.additionalLogicalOperator ?? 'AND')
        : String(options?.logicalOperator || 'AND');
    });
    if (options?.lastLogicalOperator !== undefined) {
      dynamicRules[dynamicRules.length - 1].logicalOperator = String(
        options.lastLogicalOperator,
      );
    }

    const trailingNames = new Set(
      (Array.isArray(options?.insertBefore) ? options.insertBefore : ['limit', 'offset', 'order by'])
        .map((name: unknown) => String(name).trim().toLowerCase()),
    );
    const insertionIndex = baseFilters.findIndex((rule: any) =>
      trailingNames.has(String(rule?.attributeName || '').trim().toLowerCase()),
    );
    const index = insertionIndex >= 0 ? insertionIndex : baseFilters.length;
    return [
      ...baseFilters.slice(0, index),
      ...dynamicRules,
      ...baseFilters.slice(index),
    ];
  }

  /** Rename UI/global-filter keys before they are merged into requestData. */
  private remapRequestParams(
    requestParams: unknown,
    keyMap: unknown,
  ): Record<string, any> {
    if (!requestParams || typeof requestParams !== 'object' || Array.isArray(requestParams)) {
      return {};
    }

    const mappings = keyMap && typeof keyMap === 'object' && !Array.isArray(keyMap)
      ? keyMap as Record<string, unknown>
      : {};

    return Object.entries(requestParams as Record<string, any>).reduce<Record<string, any>>(
      (result, [key, value]) => {
        const mappedKey = String(mappings[key] ?? key).trim();
        if (mappedKey) result[mappedKey] = value;
        return result;
      },
      {},
    );
  }

  /** Keep explicitly configured request fields alongside a generated filters array. */
  private pickRequestParams(
    requestParams: Record<string, any>,
    keys: unknown,
  ): Record<string, any> {
    const allowedKeys = new Set(
      (Array.isArray(keys) ? keys : [])
        .map((key) => String(key).trim())
        .filter(Boolean),
    );
    return Object.fromEntries(
      Object.entries(requestParams).filter(([key]) => allowedKeys.has(key)),
    );
  }

  createPayload(
    filters: { [key: string]: any } = {},
    entity: string = '',
    filterName: string = '',
  ): any {
    const keys = Object.keys(filters);

    const where = keys.flatMap((key) => {
      const value = filters[key];

      // Check if the key is 'filter' and its value is an array of rule objects
      const isFilterRuleArray =
        key === 'filter' &&
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'attributeName' in value[0];

      if (isFilterRuleArray) {
        return value; // It's from the dialog, return the rules directly
      }

      // Process other simple filters (or a 'filter' that is not a rule array)
      const values = (Array.isArray(value) ? value : [value]).filter(
        (v) => v !== null && v !== undefined && v !== '' && v !== 'All',
      );

      if (values.length === 0) {
        return [];
      }

      return [
        {
          attributeName: key,
          operator: values.length > 1 ? 'in' : '=',
          attributeValues: values,
          logicalOperator: 'AND', // Placeholder
        },
      ];
    });

    // Now, fix the logical operators for the entire 'where' clause
    if (where.length > 0) {
      for (let i = 0; i < where.length - 1; i++) {
        // If a rule from the dialog already has an operator (like OR), respect it.
        // Otherwise, set it to AND.
        if (!where[i].logicalOperator) {
          where[i].logicalOperator = 'AND';
        }
      }
      // The very last item in the whole clause has no logical operator.
      where[where.length - 1].logicalOperator = '';
    }

    return {
      filterName,
      entity,
    };
  }

  getApi(url: string, filters?: any, data?: any, method: 'post' | 'get' = 'post'): Observable<any> {
    const queryParamsForHttp = { ...(filters?.queryParams || {}) };
    const rawQueryString = filters?.rawQueryString;
    const requestHeaders = this.resolveHeaders(data, filters);

    //  Embed query params into URL path
    if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
      const embeddedParts: string[] = [];

      for (const paramName of data.embeddedParams) {
        if (queryParamsForHttp[paramName]) {
          embeddedParts.push(queryParamsForHttp[paramName]);
          delete queryParamsForHttp[paramName];
        }
      }

      if (embeddedParts.length > 0) {
        const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        url = [baseUrl, ...embeddedParts].join('/');
      }
    }

    //  PAYLOAD LOGIC
    let payload: any = null;
    if (data?.rawPayload) {
      payload = JSON.parse(JSON.stringify(data.rawPayload));
    }

    // Use defaultPayload only if no dynamic requestParams are provided
    const excludedRequestKeys = this.resolveExcludedRequestKeys(data);
    const filteredReqParams = this.cleanRequestParams(
      filters?.requestParams,
      excludedRequestKeys,
    );
    const filteredCustomPayload = this.cleanRequestParams(
      data?.customPayload,
      excludedRequestKeys,
    );
    const hasFilteredReqParams = Object.keys(filteredReqParams).length > 0;
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};

    if (!payload && data?.skipDefaultPayload) {
      //  Do NOT use base payload
      // Send only custom payload or empty object
      payload = data?.customPayload || {};
    } else if (!payload) {
      //  USE BASE PAYLOAD
      payload = {
        requestData: {
          ...(!hasFilteredReqParams ? defaultRequestData : {}),
          ...filteredReqParams,
          ...filteredCustomPayload,
        },
        responseList: [],
        entity: data?.entity || 'default_entity',
        workflowId: data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: data?.functionName || 'DefaultService',
            version: data?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: '1.0',
      };
    }

    // Query Params
    const useQueryParams = data?.useQueryParam !== false;
    let queryParams = new HttpParams();

    if (useQueryParams) {
      if (rawQueryString) {
        url = `${url}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    // Prepend Base URL only for relative API paths.
    url = this.resolveRequestUrl(url);

    // API Call
    const requestMethod = String(method || data?.method || 'post').toLowerCase();
    const cacheKey = `${requestMethod}|${url}|${JSON.stringify(payload)}|${queryParams.toString()}`;

    if (this.inFlightRequests.has(cacheKey)) {
      return this.inFlightRequests.get(cacheKey)!;
    }

    const request$ = (
      requestMethod === 'post'
        ? this.http.post<any>(url, payload, { params: queryParams, headers: requestHeaders })
        : this.http.get<any>(url, { params: queryParams, headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay(1),
      finalize(() => this.inFlightRequests.delete(cacheKey))
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }

  getDropdownOptions(
    url: string,
    payload: any,
    method: string = 'post',
    headers?: Record<string, string>,
  ): Observable<any> {
    url = this.resolveRequestUrl(url);
    const normalizedMethod = (method || 'post').toLowerCase();
    const requestHeaders = this.resolveHeaders({ headers });
    const dedupKey = `DROPDOWN|${normalizedMethod.toUpperCase()}|${url}|${JSON.stringify(payload)}|${JSON.stringify(requestHeaders)}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = (
      normalizedMethod === 'get'
        ? this.http.get<any>(url, { headers: requestHeaders })
        : this.http.post<any>(url, payload, { headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  protected unwrapResponse(response: any): any {
    if (response?.responseList?.length > 0) {
      const firstItem = response.responseList[0];
      if (firstItem?.data?.length > 0) {
        return firstItem.data[0];
      }
    }
    return response;
  }

  getData(url: string) {
    return this.http.get(url);
  }
}
